<template>
  <div class="shipment-rate-shop-tab" :class="tab.key">
    <SpotMarketTable
      id="spot-markets"
      :spot-markets="spotMarkets"
      :selectable="!disabled && isRateable"
      @rate-selected="emit('rate-selected', $event)"
      @spot-market-cancelled="onSpotMarketCancelled"
      @spot-market-resent="onSpotMarketResent"
    >
      <template #empty>
        {{
          t('common.noParam', {
            param: t('carrierPortal.spotMarket.spotMarkets', 2),
          })
        }}
      </template>
    </SpotMarketTable>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useSpotMarketPolling } from '@/composables';
import {
  RateQuote,
  RateShop,
  Shipment,
  ShipmentStatus,
  SpotMarket,
} from '@/model';
import { useSpotMarketStore } from '@/stores/SpotMarket';

import SpotMarketTable from '@/components/rateShop/SpotMarketTable.vue';
import { ShipmentRateShopTab } from '@/components/shipment/rateShop/ShipmentRateShopHeader.vue';

const props = defineProps({
  tab: {
    type: Object as PropType<ShipmentRateShopTab>,
    required: true,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
  spotMarkets: {
    type: Object as PropType<Loadable<SpotMarket[]>>,
    default: () => ({ loading: false }),
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { tab, shipment, rateShop, spotMarkets, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'rate-selected', rateQuote: RateQuote): void;
  (event: 'spot-market-cancelled', spotMarketId: number): void;
  (event: 'spot-market-resent', spotMarketId: number): void;
}>();

const { t } = useI18n();
const toast = useToast();
const { loadSpotMarkets } = useSpotMarketStore();
const { startSpotMarketPolling } = useSpotMarketPolling();

const isRateable = computed(() => {
  return shipment.value.status === ShipmentStatus.PLANNING;
});

const onSpotMarketResent = async (spotMarketId: number) => {
  const shipmentId = shipment.value.id;
  const rateShopId = rateShop.value.data?.id;
  if (!shipmentId || !rateShopId) {
    return;
  }
  try {
    await api.shipment.resendSpotMarket(shipmentId, spotMarketId);
    await loadSpotMarkets(rateShopId, {
      bypassCache: true,
      shipmentId,
    });
    startSpotMarketPolling(rateShopId, { shipmentId });
    emit('spot-market-resent', spotMarketId);
  } catch (e) {
    toast.addError({
      detail: t('errors.resendingParam', {
        param: t('carrierPortal.spotMarket.spotMarkets', 1),
      }),
      error: e,
    });
  }
};

const onSpotMarketCancelled = async (spotMarketId: number) => {
  const shipmentId = shipment.value.id;
  const rateShopId = rateShop.value.data?.id;
  if (!shipmentId || !rateShopId) {
    return;
  }
  try {
    await api.shipment.cancelSpotMarket(shipmentId, spotMarketId);
    await loadSpotMarkets(rateShopId, {
      bypassCache: true,
      shipmentId,
    });
    emit('spot-market-cancelled', spotMarketId);
  } catch (e) {
    toast.addError({
      detail: t('errors.cancelingParam', {
        param: t('carrierPortal.spotMarket.spotMarkets', 1),
      }),
      error: e,
    });
  }
};
</script>
